import React, { useState, useEffect, useRef } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { Card, Row, Col, Statistic, Tag, Descriptions, Alert, Space, Button, Spin, message } from 'antd';
import {
  GlobalOutlined,
  SafetyOutlined,
  LockOutlined,
  ThunderboltOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  LineChartOutlined
} from '@ant-design/icons';
import apiClient from '../../../shared/services/apiClient';
import dayjs from 'dayjs';
import * as echarts from 'echarts';

export default function Overview() {
  const navigate = useNavigate();
  const { domainData } = useOutletContext();

  const [chartLoading, setChartLoading] = useState(false);
  const [trafficData, setTrafficData] = useState(null);
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  // 加载最近7天的流量数据
  useEffect(() => {
    if (domainData?.domain) {
      loadTrafficData();
    }
  }, [domainData]);

  // 初始化/更新图表
  useEffect(() => {
    if (chartRef.current && trafficData && (Array.isArray(trafficData) ? trafficData.length > 0 : (trafficData?.timeline || trafficData?.trend))) {
      initChart();
    }
    return () => {
      if (chartInstance.current) {
        chartInstance.current.dispose();
        chartInstance.current = null;
      }
    };
  }, [trafficData]);

  // 窗口resize时更新图表
  useEffect(() => {
    const handleResize = () => chartInstance.current?.resize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loadTrafficData = async () => {
    setChartLoading(true);
    try {
      const startTime = dayjs().subtract(6, 'day').startOf('day').format('YYYY-MM-DD HH:mm:ss');
      const endTime = dayjs().format('YYYY-MM-DD HH:mm:ss');
      const res = await apiClient.get('/api/admin/traffic/timeline', {
        params: { startTime, endTime, domain_id: domainData.id }
      });
      if (res.data.success) {
        setTrafficData(res.data.data);
      } else {
        message.error('流量趋势加载失败：' + (res.data.error || '未知错误'));
      }
    } catch (error) {
      message.error('流量趋势加载失败：' + (error.response?.data?.error || error.message));
    } finally {
      setChartLoading(false);
    }
  };

  const initChart = () => {
    if (!chartRef.current) return;

    if (chartInstance.current) {
      chartInstance.current.dispose();
    }

    chartInstance.current = echarts.init(chartRef.current);

    const trend = Array.isArray(trafficData) ? trafficData : (trafficData.timeline || trafficData.trend || []);
    const dates = trend.map(item => {
      const t = item.time_bucket || item.date || item.time;
      if (!t) return t;
      // OpenObserve time_bucket 是 UTC，转换为本地时间显示
      const utcStr = typeof t === 'string' && !t.endsWith('Z') && !t.includes('+') ? t + 'Z' : t;
      return dayjs(new Date(utcStr)).format('MM-DD HH:mm');
    });
    const requests = trend.map(item => item.requests || item.count || 0);
    const bandwidth = trend.map(item => {
      const bytes = item.traffic || item.bandwidth || item.bytes || 0;
      return (bytes / 1024 / 1024).toFixed(2);
    });

    const option = {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'cross' }
      },
      legend: {
        data: ['请求数', '流量(MB)'],
        top: 0
      },
      grid: {
        left: 50,
        right: 50,
        bottom: 30,
        top: 40,
        containLabel: false
      },
      xAxis: {
        type: 'category',
        data: dates
      },
      yAxis: [
        {
          type: 'value',
          name: '请求',
          position: 'left'
        },
        {
          type: 'value',
          name: 'MB',
          position: 'right'
        }
      ],
      series: [
        {
          name: '请求数',
          type: 'line',
          data: requests,
          smooth: true,
          itemStyle: { color: '#1890ff' },
          areaStyle: { color: 'rgba(24, 144, 255, 0.1)' }
        },
        {
          name: '流量(MB)',
          type: 'line',
          yAxisIndex: 1,
          data: bandwidth,
          smooth: true,
          itemStyle: { color: '#52c41a' },
          areaStyle: { color: 'rgba(82, 196, 26, 0.1)' }
        }
      ]
    };

    chartInstance.current.setOption(option);
  };

  const getStatusTag = (enabled) => {
    return enabled ? (
      <Tag icon={<CheckCircleOutlined />} color="success">启用</Tag>
    ) : (
      <Tag icon={<CloseCircleOutlined />} color="default">禁用</Tag>
    );
  };

  return (
    <div style={{ padding: '24px 0' }}>
      <h2 style={{ marginBottom: 24 }}>域名概览</h2>

      {/* 警告信息 */}
      {!domainData.ssl_enabled && (
        <Alert
          message="SSL/TLS 未启用"
          description="建议启用 SSL/TLS 以保护您的网站数据传输安全"
          type="warning"
          showIcon
          closable
          style={{ marginBottom: 24 }}
        />
      )}

      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="源站数量"
              value={domainData.hostnames?.length || domainData.hostname_count || 0}
              prefix={<GlobalOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="规则数量"
              value={domainData.rules?.length || domainData.rule_count || 0}
              prefix={<ThunderboltOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="SSL 证书"
              value={(domainData.has_ssl || domainData.ssl_enabled) ? '已配置' : '未配置'}
              prefix={<LockOutlined />}
              valueStyle={{ color: (domainData.has_ssl || domainData.ssl_enabled) ? '#3f8600' : '#cf1322' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="防火墙"
              value={domainData.waf_enabled ? '已启用' : '已禁用'}
              prefix={<SafetyOutlined />}
              valueStyle={{ color: domainData.waf_enabled ? '#3f8600' : '#999' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 流量趋势图 */}
      <Card
        title={
          <span>
            <LineChartOutlined style={{ marginRight: 8 }} />
            最近 7 天流量
          </span>
        }
        style={{ marginBottom: 24 }}
        extra={
          <Button type="link" onClick={() => navigate('traffic')}>
            详情
          </Button>
        }
      >
        <Spin spinning={chartLoading}>
          <div style={{ position: 'relative' }}>
            <div
              ref={chartRef}
              style={{ width: '100%', height: 300, minHeight: 300 }}
            />
            {!chartLoading && !(Array.isArray(trafficData) ? trafficData.length : (trafficData?.timeline?.length || trafficData?.trend?.length)) && (
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                color: '#999'
              }}>
                暂无流量数据
              </div>
            )}
          </div>
        </Spin>
      </Card>

      {/* 域名基本信息 */}
      <Card title="基本信息" style={{ marginBottom: 24 }}>
        <Descriptions bordered column={2}>
          <Descriptions.Item label="域名">
            {domainData.domain}
          </Descriptions.Item>
          <Descriptions.Item label="所属用户">
            {domainData.user_email || domainData.user?.email || (domainData.user_id ? `#${domainData.user_id}` : '-')}
          </Descriptions.Item>
          <Descriptions.Item label="创建时间">
            {domainData.created_at}
          </Descriptions.Item>
          <Descriptions.Item label="更新时间">
            {domainData.updated_at || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="SSL/TLS">
            {getStatusTag(domainData.ssl_enabled)}
          </Descriptions.Item>
          <Descriptions.Item label="WAF 防火墙">
            {getStatusTag(domainData.waf_enabled)}
          </Descriptions.Item>
          <Descriptions.Item label="缓存模式">
            {domainData.cache_enable || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="配置版本">
            {domainData.config_version || '-'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* 源站配置信息 */}
      <Card title="源站配置" style={{ marginBottom: 24 }}>
        {domainData.hostnames && domainData.hostnames.length > 0 ? (
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            {domainData.hostnames.map((hostname, index) => (
              <Card
                key={index}
                type="inner"
                title={`源站 ${index + 1}: ${hostname.hostname}`}
                extra={
                  <Tag color={hostname.enabled !== false ? 'green' : 'default'}>
                    {hostname.enabled !== false ? '启用' : '禁用'}
                  </Tag>
                }
              >
                <Descriptions column={2} size="small">
                  <Descriptions.Item label="回源地址">
                    {Array.isArray(hostname.origin) ? hostname.origin.join(', ') : hostname.origin}
                  </Descriptions.Item>
                  <Descriptions.Item label="协议">
                    {hostname.protocol}
                  </Descriptions.Item>
                  <Descriptions.Item label="端口">
                    {hostname.port || '默认'}
                  </Descriptions.Item>
                  <Descriptions.Item label="负载均衡">
                    {hostname.mode}
                  </Descriptions.Item>
                  <Descriptions.Item label="HTTP 版本">
                    {hostname.http_version}
                  </Descriptions.Item>
                  <Descriptions.Item label="WebSocket">
                    {hostname.websocket_enabled == null ? <Tag>跟随域名</Tag> : getStatusTag(hostname.websocket_enabled)}
                  </Descriptions.Item>
                  <Descriptions.Item label="代理超时">
                    {hostname.proxy_timeout}秒
                  </Descriptions.Item>
                  <Descriptions.Item label="优先级">
                    {hostname.priority}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            ))}
          </Space>
        ) : (
          <Alert
            message="未配置源站"
            description="请先配置至少一个源站服务器"
            type="info"
            showIcon
          />
        )}
      </Card>

      {/* 页面规则信息 */}
      {domainData.rules && domainData.rules.length > 0 && (
        <Card title="页面规则">
          <Descriptions bordered column={1}>
            {domainData.rules.map((rule, index) => (
              <Descriptions.Item key={index} label={`规则 ${index + 1}`}>
                <div>
                  <strong>{rule.name}</strong>
                  <div style={{ marginTop: 8 }}>
                    优先级: {rule.priority}
                  </div>
                </div>
              </Descriptions.Item>
            ))}
          </Descriptions>
        </Card>
      )}
    </div>
  );
}
